/**
 * Copyright 2023 Design Barn Inc.
 */

'use client';

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useClient } from 'urql';

import { graphql } from '../../gql';
import Bugsnag from '../libs/bugsnag';
import type { ClevertapUserProfile } from '../libs/clevertap';
import { initCleverTap, loginUser, pushSignUpEvent, resetClevertapIdentity, ctLog } from '../libs/clevertap';
import type { ISessionResponse } from '../utils/api-requests';
import { deimosGetSession } from '../utils/api-requests';
import urlJoin from '../utils/url-join';
import { getFormattedSearchParamsString } from '../utils/url-params';

import { useConsent } from './consent-context';
import { FeatureFlags, getGrowthBookInstance } from './feature-flags-context';
import { useIframeMessaging } from './iframe-messaging-context';

if (!process.env['NEXT_PUBLIC_DEIMOS_SESSION_URL']) {
  throw new Error('NEXT_PUBLIC_DEIMOS_SESSION_URL  environment variable is not set');
}

export interface AuthResult {
  accessToken: string;
  additionalAuthRequired: boolean;
  expiresAt: string;
  socialLoginAccountCreated: boolean | null;
  tokenType: 'Bearer' | 'Cookie';
}

type AuthMode = 'login' | 'register';

export interface AuthSessionContextValue {
  handleAuthResult: ({
    mode,
    result,
    verifyEmail,
  }: {
    mode: AuthMode;
    result: AuthResult;
    verifyEmail?: boolean;
  }) => Promise<void>;
  isRegistering: boolean;
  isSessionChecked: boolean;
  loading: boolean;
  session: ISessionResponse | null;
}

const AuthSessionContext = createContext<AuthSessionContextValue>({
  loading: false,
  isRegistering: false,
  isSessionChecked: false,
  session: null,
  handleAuthResult: async () => Promise.resolve(),
});

const viewerQuery = graphql(`
  query viewerWithProfile {
    viewer {
      id
      email
      emailVerified
      firstName
      lastName
      country
      city
      behanceUsername
      dribbbleUsername
      isHireable
      twitterUsername
      linkedinUsername
      instagramUsername
      website
      githubUsername
      username
      avatarUrl
      userSegments {
        id
        title
      }
    }
  }
`);

export const AuthSessionProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [session, setSession] = useState<ISessionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSessionChecked, setIsSessionChecked] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const client = useClient();
  const { afterLogin, afterRegister, closeModal, config, openAuthUrlInParent } = useIframeMessaging();
  const { metadata, origin, returnToPath, returnToUrl } = config;

  const router = useRouter();
  const pathname = usePathname();

  const { hasMarketingConsent, isConsentInitialized, isMarketingConsentRequired, waitForConsentInitialization } =
    useConsent();
  const consentStateRef = React.useRef({
    hasMarketingConsent,
    isConsentInitialized,
    isMarketingConsentRequired,
  });

  consentStateRef.current = {
    hasMarketingConsent,
    isConsentInitialized,
    isMarketingConsentRequired,
  };

  const getBaseUrl = (url: string | undefined): string => {
    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const baseUrl = url ? new URL(url).origin : '';

    return baseUrl;
  };

  // this function checks if the user's email is verified
  // if the user's email is not verified, it will redirect to the verify-email page
  const checkIsEmailVerified = useCallback(
    async (token: string) => {
      const queryResult = await client.query(
        viewerQuery,
        {},
        {
          fetchOptions: {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      );

      return queryResult.data?.viewer.emailVerified;
    },
    [client],
  );

  // this function is run on init and checks whether the user has an existing session
  // if the user has a session, it will set the session and close the modal except in the
  // case of logout, which needs the modal to stay open to complete the logout process
  const initAuthSession = async (): Promise<void> => {
    const deimosSession = await deimosGetSession();

    setIsSessionChecked(true);
    setLoading(false);

    if (deimosSession && deimosSession.token) {
      setSession(deimosSession);

      // we need to keep the modal open if it's a logout
      // so that the actions in the logout page can be completed
      // then the page will trigger closing the modal
      if (pathname.includes('/logout')) {
        return;
      }

      const verified = await checkIsEmailVerified(deimosSession.token);

      if (verified) {
        closeModal();
      }
    }
  };

  const baseUrl = getBaseUrl(origin);

  const returnTo = returnToUrl || urlJoin(baseUrl, returnToPath || '');

  // this is run once on the initial mount of the auth iframe app
  useEffect(() => {
    setLoading(true);

    initAuthSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // this function is run with the results of an authentication request
  const handleAuthResult = useCallback(
    async ({
      mode,
      result,
      verifyEmail,
    }: {
      mode: AuthMode;
      result: AuthResult;
      verifyEmail?: boolean;
    }): Promise<void> => {
      if (mode === 'register') {
        setIsRegistering(true);
      }

      try {
        const params = getFormattedSearchParamsString(searchParams);

        if (result.tokenType.toLowerCase() !== 'cookie') {
          return;
        }

        // handle any prerequisite steps before getting the session
        if (result.additionalAuthRequired) {
          const mfaLinkHref = `/mfa${params}`;

          router.push(mfaLinkHref);

          return;
        }

        const sessionData = await deimosGetSession();

        if (!sessionData) {
          throw new Error('Internal server error: failed to validate session.');
        }

        if (!sessionData.active || !sessionData.expires_at || !sessionData.token) {
          throw new Error('Internal server error: invalid session response.');
        }

        if (mode === 'register') {
          ctLog('handleAuthResult: consent state:', {
            isConsentInitialized: consentStateRef.current.isConsentInitialized,
            isMarketingConsentRequired: consentStateRef.current.isMarketingConsentRequired,
            hasMarketingConsent: consentStateRef.current.hasMarketingConsent,
          });
        }

        if (mode === 'register' && !consentStateRef.current.isConsentInitialized) {
          ctLog('handleAuthResult: consent not yet initialized, waiting...');
          await waitForConsentInitialization();
          ctLog('handleAuthResult: consent initialized, state now:', {
            isMarketingConsentRequired: consentStateRef.current.isMarketingConsentRequired,
            hasMarketingConsent: consentStateRef.current.hasMarketingConsent,
          });
        }

        // Handle Clevertap integration for registration when marketing tracking is allowed
        const shouldRunClevertap =
          mode === 'register' &&
          (!consentStateRef.current.isMarketingConsentRequired || consentStateRef.current.hasMarketingConsent);

        if (mode === 'register' && !shouldRunClevertap) {
          ctLog('handleAuthResult: CleverTap SKIPPED — marketing consent not granted');
        }

        if (shouldRunClevertap) {
          try {
            ctLog('handleAuthResult: starting CleverTap flow');

            // Initialize Clevertap SDK (idempotent, safe to call if already initialized)
            await initCleverTap();

            // Reset identity state using SDK-native clear() to ensure the in-memory LRU cache
            // and localStorage are consistent. This prevents onUserLogin from silently skipping
            // the profile push when stale identity data exists.
            await resetClevertapIdentity();

            // Fetch viewer data via GraphQL with fresh token
            ctLog('handleAuthResult: fetching viewer data via GraphQL');
            const viewerResult = await client.query(
              viewerQuery,
              {},
              {
                fetchOptions: {
                  headers: {
                    Authorization: `Bearer ${sessionData.token}`,
                  },
                },
              },
            );

            if (viewerResult.data?.viewer) {
              const viewer = viewerResult.data.viewer;

              ctLog('handleAuthResult: viewer fetched, id:', viewer.id, 'email:', viewer.email);

              // Build Clevertap profile from viewer data
              const userProfileUrl = process.env['NEXT_PUBLIC_USER_PROFILE_URL'] || 'https://lottiefiles.com';
              const profileUrl = viewer.username ? `${userProfileUrl}/${viewer.username}` : undefined;

              const clevertapProfile: ClevertapUserProfile = {
                Identity: viewer.id,
                Email: viewer.email ?? undefined,
                Name: [viewer.firstName, viewer.lastName].filter(Boolean).join(' ') || undefined,
                Photo: viewer.avatarUrl || undefined,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                first_name: viewer.firstName || undefined,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                last_name: viewer.lastName ?? undefined,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                email_verified: viewer.emailVerified ?? undefined,
                country: viewer.country ?? undefined,
                city: viewer.city ?? undefined,
                location: [viewer.city, viewer.country].filter(Boolean).join(', ') || undefined,
                behance: viewer.behanceUsername ?? undefined,
                dribbble: viewer.dribbbleUsername ?? undefined,
                github: viewer.githubUsername ?? undefined,
                twitter: viewer.twitterUsername ?? undefined,
                linkedin: viewer.linkedinUsername ?? undefined,
                instagram: viewer.instagramUsername ?? undefined,
                website: viewer.website ?? undefined,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                is_hire: viewer.isHireable,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                profile_url: profileUrl,
                segments: viewer.userSegments?.map((seg: { title: string }) => seg.title) ?? undefined,
                uuid: viewer.id,
              };

              // PLE-1399 - Support for test groups for marketing experiment on Clevertap
              const growthbook = getGrowthBookInstance();
              const testGroupThreshold = growthbook.getFeatureValue(
                FeatureFlags.CLEVERTAP_TEST_GROUPS_ENABLED,
                'control',
              );

              if (typeof testGroupThreshold === 'number' && testGroupThreshold > 0) {
                const isInExperiment = Math.random() < testGroupThreshold;

                clevertapProfile.test_group = isInExperiment ? 'experiment' : 'control';
              }

              // PLE-1430 - Support for experimental cohorts for marketing experiment on Clevertap
              const experimentalCohortsEnabled = growthbook.isOn(FeatureFlags.CLEVERTAP_COHORTS_ENABLED);

              if (experimentalCohortsEnabled) {
                // Randomly assign a cohort between 1 and 10
                clevertapProfile.experiment_cohort = Math.floor(Math.random() * 10) + 1;
              }

              // Push to onUserLogin
              ctLog('handleAuthResult: calling loginUser with profile');
              await loginUser(clevertapProfile);

              // Send sign-up event
              ctLog('handleAuthResult: calling pushSignUpEvent for user:', viewer.id);
              await pushSignUpEvent(viewer.id);

              // Log post-push SDK state for debugging
              ctLog('handleAuthResult: CleverTap flow complete. SDK state:', {
                wzrkG: localStorage.getItem('WZRK_G'),
                wzrkK: localStorage.getItem('WZRK_K'),
                wzrkX: localStorage.getItem('WZRK_X'),
              });
            } else {
              ctLog('handleAuthResult: viewer data is null/undefined, viewerResult.error:', viewerResult.error);
            }
          } catch (error) {
            ctLog('handleAuthResult: CleverTap error:', (error as Error).message, (error as Error).stack);
            // Clevertap errors should not prevent auth flow completion
            Bugsnag.notify(error as Error);
          }
        }

        // run the email verification check if the feature is enabled
        if (verifyEmail) {
          if (!(await checkIsEmailVerified(sessionData.token))) {
            // The verification screen needs the authenticated session. Terminal
            // auth flows should not publish it because route effects can treat it
            // as a separate, existing-session login completion.
            setSession(sessionData);

            return;
          }
        }

        const notifyAuthComplete = mode === 'register' ? afterRegister : afterLogin;
        const iframeResult = await notifyAuthComplete('password', undefined, metadata);

        if (!iframeResult.success) {
          throw new Error('Internal error.');
        }

        if (returnToUrl) {
          openAuthUrlInParent(returnTo);
        }
      } finally {
        setIsRegistering(false);
      }
    },
    [
      searchParams,
      afterLogin,
      afterRegister,
      metadata,
      returnToUrl,
      router,
      checkIsEmailVerified,
      openAuthUrlInParent,
      returnTo,
      client,
      waitForConsentInitialization,
    ],
  );

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      loading,
      isRegistering,
      isSessionChecked,
      session,
      handleAuthResult,
    }),
    [loading, isRegistering, isSessionChecked, session, handleAuthResult],
  );

  return <AuthSessionContext.Provider value={contextValue}>{children}</AuthSessionContext.Provider>;
};

export const useAuthSession = (): AuthSessionContextValue => React.useContext(AuthSessionContext);
